import React, { createContext, useContext, ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ChipiSDK } from "@chipi-stack/backend";
import type { ChipiSDKConfig } from "@chipi-stack/types";

interface ChipiContextValue {
  chipiSDK: ChipiSDK;
  config: ChipiSDKConfig;
}

const ChipiContext = createContext<ChipiContextValue | null>(null);

interface ChipiProviderProps {
  children: ReactNode;
  config: ChipiSDKConfig;
}

// Default QueryClient configuration
const createDefaultQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000, // 1 minute
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          return failureCount < 3;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });

/**
 * Provider component that wraps your app and provides Chipi SDK context with QueryClient
 */
export function ChipiProvider({ children, config }: ChipiProviderProps) {
  // Create QueryClient
  const queryClient = React.useMemo(() => createDefaultQueryClient(), []);

  const chipiSDK = React.useMemo(() => new ChipiSDK(config), [config]);

  const value = React.useMemo(
    () => ({
      chipiSDK,
      config,
    }),
    [chipiSDK, config]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ChipiContext.Provider value={value}>{children}</ChipiContext.Provider>
    </QueryClientProvider>
  );
}

/**
 * Hook to access Chipi SDK context
 */
export function useChipiContext(): ChipiContextValue {
  const context = useContext(ChipiContext);

  if (!context) {
    throw new Error("useChipiContext must be used within a ChipiProvider");
  }

  return context;
}
